import { startOfQuarter, startOfYear } from 'date-fns'
import formatDate from 'date-fns/format'
import { IssueEntity } from 'types/IssueEntity'
import {
    calculateDiscount,
    getCommitmentsInPercentage,
    getIssueSubscriptionQuota,
    getIssueSubscriptionQuotaExclGuarantors,
} from '../../issues/utils'
import { filterIssueForGraphs, getDateForIssue } from '../utils'
import { TradesStat } from 'types/Instrument'

const getDataForCommitmentChart = (issues: IssueEntity[]) => {
    const seriesData = issues
        .filter((issue) => filterIssueForGraphs(issue) && issue.type === 'Rights issue')
        .map((issue) => {
            const { totalCommitment = 0, commitment = 0 } = getCommitmentsInPercentage(issue) || {}

            return {
                date: new Date(issue.subEndDate as string),
                commitment,
                totalCommitment: Math.min(totalCommitment, 100),
            }
        })

    const series = [
        {
            dataKey: 'totalCommitmentAverage',
            id: 'totalCommitment',
            label: 'Tecknings- och garantiåtagande',
        },
        {
            dataKey: 'commitmentAverage',
            id: 'commitment',
            label: 'Teckningsåtagande',
        },
    ]

    return { excludeNullValues: false, series, seriesData }
}

const getDataForSubscriptionChart = (issues: IssueEntity[]) => {
    const seriesData = issues
        .filter((issue) => filterIssueForGraphs(issue) && issue.type === 'Rights issue')
        .map((issue) => ({
            date: new Date(issue.subEndDate as string),
            subscriptionQuota: getIssueSubscriptionQuota(issue),
            subscriptionQuotaExclGuarantors: getIssueSubscriptionQuotaExclGuarantors(issue),
        }))

    const series = [
        {
            dataKey: 'subscriptionQuotaAverage',
            id: 'subscriptionQuota',
            label: 'Teckningsgrad',
        },
        {
            dataKey: 'subscriptionQuotaExclGuarantorsAverage',
            id: 'subscriptionQuotaExclGuarantors',
            label: 'Teckningsgrad ex. garanter',
        },
    ]

    return { excludeNullValues: false, series, seriesData }
}

const getDataForGuarantorFeeIssueChart = (issues: IssueEntity[]) => {
    const seriesData = issues
        .filter((issue) => filterIssueForGraphs(issue) && issue.fees?.length && issue.type === 'Rights issue')
        .map((issue) => {
            return {
                date: new Date(getDateForIssue(issue) as string),
                feeCash: issue.fees?.find(({ feeLevel }) => feeLevel === 1)?.feeCash || null,
                feeUnits: issue.fees?.find(({ feeLevel }) => feeLevel === 1)?.feeUnits || null,
            }
        })
    const series = [
        {
            dataKey: 'feeCashAverage',
            id: 'feeCash',
            label: 'Kontant ersättning',
        },
        {
            dataKey: 'feeUnitsAverage',
            id: 'feeUnits',
            label: 'Ersättning i aktier/units',
        },
    ]

    return { series, seriesData, excludeNullValues: true }
}

const filter10dPrePublication = ({ periodType, numberOfDays }: TradesStat) =>
    periodType === 'prePublication' && numberOfDays === 10

const getDataForDiscountChart = (issues: IssueEntity[]) => {
    const seriesData = issues
        .filter(
            (issue) =>
                filterIssueForGraphs(issue) &&
                issue.type === 'Rights issue' &&
                issue.tradesStats.find(filter10dPrePublication)?.vwap,
        )
        .map((issue) => {
            const vwap = issue.tradesStats.find(filter10dPrePublication)?.vwap || null
            const discount10d = calculateDiscount(issue, vwap, false)
            const discount10dTerp = calculateDiscount(issue, vwap, true)
            const date = new Date(getDateForIssue(issue) as string)

            return {
                date,
                riDiscount10dPrePublication: discount10d,
                riDiscountTerp10dPrePublication: discount10d === null || isNaN(discount10d) ? null : discount10dTerp, // terp may be NaN due to calculateDiscount
            }
        })
    const series = [
        {
            dataKey: 'riDiscount10dPrePublicationAverage',
            id: 'riDiscount10dPrePublication',
            label: 'Rabatt mot 10 dagar VWAP',
        },
        {
            dataKey: 'riDiscountTerp10dPrePublicationAverage',
            id: 'riDiscountTerp10dPrePublication',
            label: 'TERP-rabatt mot 10 dagar VWAP',
        },
    ]

    return { excludeNullValues: true, series, seriesData }
}

export const getDataForChart = (chart: string, issues: IssueEntity[]) => {
    switch (chart) {
        case 'commitment':
            return getDataForCommitmentChart(issues)
        case 'subscription':
            return getDataForSubscriptionChart(issues)
        case 'guarantor-fee-issue':
            return getDataForGuarantorFeeIssueChart(issues)
        case 'discount':
            return getDataForDiscountChart(issues)
        default:
            return { excludeNullValues: false, seriesData: [], series: [] }
    }
}

const getMinValue = (currentMinValue: number | null, value?: number | null) => {
    if (value === null || value === undefined) return currentMinValue
    if (currentMinValue === null || currentMinValue === undefined) return value

    return Math.min(value, currentMinValue)
}

const getMaxValue = (currentMinValue: number | null, value?: number | null) => {
    if (value === null || value === undefined) return currentMinValue
    if (currentMinValue === null || currentMinValue === undefined) return value

    return Math.max(value, currentMinValue)
}

type TrendChartDatasetBase = { [key: string]: number | string | Date | null } & {
    count: number
    date: Date
    id: string
    time: string
}

export const groupByTimeUnit = (
    dataset: Omit<TrendChartDatasetBase, 'id' | 'time'>[],
    timeUnit: 'quarter' | 'year',
    valueKeys: string[],
    excludeNullValues: boolean,
) =>
    Object.values(
        dataset.reduce(
            (groups, item) => {
                const groupKey = formatDate(item.date as Date, timeUnit === 'quarter' ? 'yyyy-q' : 'yyyy')
                const group = groups[groupKey] || {}
                const values = valueKeys.reduce((acc, key) => {
                    if (excludeNullValues && item[key] === null) return acc

                    const countKey = `${key}Count`
                    const sumKey = `${key}Sum`
                    const aveKey = `${key}Average`
                    const minKey = `${key}Min`
                    const maxKey = `${key}Max`

                    const sum = ((group[sumKey] as number) || 0) + (item[key] as number)
                    const count = ((group[countKey] as number) || 0) + 1

                    return {
                        ...acc,
                        [countKey]: count,
                        [sumKey]: sum,
                        [aveKey]: sum / count,
                        [maxKey]: getMaxValue(group[maxKey] as number, item[key] as number),
                        [minKey]: getMinValue(group[minKey] as number, item[key] as number),
                    }
                }, {})

                return {
                    ...groups,
                    [groupKey]: {
                        ...group,
                        id: groupKey,
                        date:
                            timeUnit === 'quarter'
                                ? startOfQuarter(new Date(item.date as Date))
                                : startOfYear(new Date(item.date as Date)),
                        time: '',
                        ...values,
                    },
                }
            },
            {} as Record<string, TrendChartDatasetBase>,
        ),
    ).sort((a, b) => (a.date < b.date ? -1 : 1))

export const formatSeries = (dataset: TrendChartDatasetBase[], timeUnit: 'quarter' | 'year') =>
    dataset.map((data) => {
        const insufficientDataKeys = Object.keys(data)
            .filter((key) => /Count$/.test(key) && data[key] !== null && (data[key] as number) < 3)
            .map((key) => key.replace(/Count$/, ''))
        const resetedValues = insufficientDataKeys.reduce((acc, key) => ({ ...acc, [key]: null }), {})

        return {
            ...data,
            ...resetedValues,
            time: formatDate(data.date, timeUnit === 'quarter' ? 'yyyy QQQ' : 'yyyy'),
        }
    })
